import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { Loader } from "Components/General/Loader";
import { Button, Row, Col, Input, Select, Form } from "antd";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";

interface ProjectViewProps extends WithTranslation, WithRouterProps {
  groupId?: string;
}

interface ProjectViewState {
  templates: API_AdminProject[];
  loading: boolean;
  submitting: boolean;
}

export const PGAdminForm = withTranslation([I18N_NS.PROJECT, I18N_NS.GENERAL])(
  withRouter(
    observer(
      class ProjectView extends React.Component<ProjectViewProps, ProjectViewState> {
        constructor(props: ProjectViewProps) {
          super(props);
          this.state = {
            templates: [],
            loading: false,
            submitting: false,
          };
        }

        componentDidMount() {
          const { t, navigate, groupId } = this.props;
          (async () => {
            this.setState({ loading: true });
            try {
              const { items }: API_MultiAdminProjectResponse = await API.getData(
                API_URL.ADMIN_TEMPLATES.getAllTemplates()
              );
              if (items) {
                this.setState({ templates: items, loading: false });
              }
            } catch (err) {
              navigate(`/admin/pg/${groupId}/projects`);
              notificationError(t("Error"), t(`${I18N_NS.GENERAL}:::Something technical went wrong.`));
              throw err;
            }
          })();
        }

        handleSubmit = async (values: API_ProjectGroupAdminProject) => {
          const { t, navigate, groupId } = this.props;
          try {
            values.projectGroupId = +groupId;
            this.setState({ submitting: true });
            const response: API_AdminProject = await API.newData(
              API_URL.ADMIN_PROJECTS.createProjectForPGAdmin(),
              values
            );
            if (response.id) {
              notificationSuccess(
                t("Success"),
                t("New project created {{ name }}", {
                  name: response.name,
                  interpolation: { escapeValue: false },
                })
              );
              navigate(`/admin/pg/${groupId}/projects`);
            }
          } catch (err) {
            throw err;
          } finally {
            this.setState({ submitting: false });
          }
        };

        get prGroupTemplates() {
          return this.state.templates.map((val) => {
            return (
              <Select.Option key={`${val.id}`} value={val.id.toString()}>
                {val.name}
              </Select.Option>
            );
          });
        }

        render() {
          const { t, navigate, groupId } = this.props;
          const { loading, submitting } = this.state;
          if (loading) {
            return <Loader />;
          }

          return (
            <div className="g-card">
              <h1>{t("New Project")}</h1>
              <div className="mb-2">
                <Form onFinish={this.handleSubmit} className="project-config-form">
                  <Row gutter={20}>
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label={t("Name")}
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: t("Please provide a project name"),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label={t("Short name")}
                        name="shortname"
                        rules={[
                          {
                            required: true,
                            message: t("Please provide a short name"),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label={t("Description")}
                        name="description"
                        rules={[
                          {
                            required: true,
                            message: t("Please provide a description"),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label={t("Select template project")}
                        name="templateId"
                        valuePropName="selected"
                        rules={[
                          {
                            required: true,
                            message: t("Please provide a template project"),
                          },
                        ]}
                      >
                        <Select allowClear={true} showSearch={true} optionFilterProp="children">
                          {this.prGroupTemplates}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row justify="space-between" style={{ marginTop: "20px" }}>
                    <Button onClick={() => navigate(`/admin/pg/${groupId}/projects`)}>
                      {t(`${I18N_NS.GENERAL}:::Cancel`)}
                    </Button>
                    <Button loading={submitting} type="primary" htmlType="submit">
                      {t("Create")}
                    </Button>
                  </Row>
                </Form>
              </div>
            </div>
          );
        }
      }
    )
  )
);
